import { ElMessage } from 'element-plus';
import { inject, ref, type Ref } from 'vue';
import { HTTP_REQ } from '../../utils';
import type { NormalResponse, TableResponse } from '../../types';

type UseBaseTableResult<T = any> = TableResponse<T> & NormalResponse<T>;

/**
 * 基础的表格数据获取hooks
 * @param api 接口地址，请求方式
 * @param options 配置
 *  - method 请求方式，默认为GET
 *  - paging 是否分页。是：数据在rows中;否：数据在data中
 */
export const useBaseTable = <T = any[]>(
  api: string | Ref<string>,
  options?: {
    // 请求方式，默认为GET
    method?: 'GET' | 'POST' | 'get' | 'post';
    // 是否分页
    // 是：数据在rows中;否：数据在data中
    paging: boolean;
  },
) => {
  const tableData = ref<T>([] as T);
  const _http = inject(HTTP_REQ);
  const loading = ref(false);
  const pagination = ref({
    currentPage: 1,
    pageSize: 20,
    small: false,
    disabled: false,
    background: true,
    total: 0,
    pagerCount: 7,
    layout: 'total, sizes, prev, pager, next, jumper',
    pageSizes: [20, 40, 60],
    align: 'right',
  });

  async function GetTableData<T = any[]>(query?: Record<string, any>) {
    loading.value = true;
    let params: Record<string, any> = query || {};
    if (options?.paging) {
      // 添加分页
      params = Object.assign(params, {
        pageNum: pagination.value.currentPage,
        pageSize: pagination.value.pageSize,
      });
    }
    if (_http) {
      try {
        const { code, data, rows, total, msg } = await _http.request<UseBaseTableResult<T>>(
          options?.method || 'GET',
          typeof api === 'string' ? api : api.value,
          {
            params,
          },
        );
        if (code === 200) {
          if (options?.paging) {
            pagination.value.total = total || 0;
            tableData.value = rows as T;
          } else {
            pagination.value.total = 0;
            tableData.value = data as T;
          }
        } else {
          ElMessage.error(msg || '数据获取失败!');
          resetTable();
        }
      } catch (e) {
        console.error(e);
        ElMessage.error(String(e) || '数据获取失败!');
        resetTable();
      }
    } else {
      console.error('http request is not found!');
      resetTable();
    }
    loading.value = false;
  }

  async function handleSizeChange(val: number, query?: Record<string, any>) {
    pagination.value.pageSize = val;
    await GetTableData(query);
  }

  async function handleCurrentChange(val: number, query?: Record<string, any>) {
    pagination.value.currentPage = val;
    await GetTableData(query);
  }

  function resetTable() {
    pagination.value.currentPage = 1;
    pagination.value.total = 0;
    tableData.value = [];
  }

  return {
    loading,
    tableData,
    pagination,
    resetTable,
    GetTableData,
    handleSizeChange,
    handleCurrentChange,
  };
};
